import React, { useState, useRef } from 'react';
import api, { API_URL } from './api';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

const Container = styled.div`
  max-width: 900px;
  margin: 10px auto;
  padding: 15px;
  font-family: 'Inter', 'Segoe UI', sans-serif;
`;

const Title = styled.h3`
  color: #1a1a1a;
  margin-bottom: 15px;
  font-size: 1 rem;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Card = styled.div`
  background: white;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  margin-bottom: 20px;
  border: 1px solid #eee;
`;

const DropArea = styled.div`
  width: 100%;
  height: 120px;
  border: 2px dashed #cbd5e1;
  background-color: #f8fafc;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
  border-radius: 12px;
  transition: all 0.3s;
  color: #64748b;
  cursor: pointer;

  &:hover {
    background-color: #f1f5f9;
    border-color: #3b82f6;
    color: #3b82f6;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  margin: 5px;
  background-color: #2563eb;
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #1d4ed8;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #94a3b8;
    cursor: not-allowed;
    transform: none;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ResponsiveTable = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: 10px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  border: 1px solid #eee;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
  background: white;

  th {
    background-color: #f8fafc;
    color: #475569;
    font-weight: 600;
    padding: 14px;
    text-align: center;
    font-size: 0.85rem;
    border-bottom: 2px solid #e2e8f0;
  }

  td {
    padding: 14px;
    text-align: center;
    font-size: 0.9rem;
    border-bottom: 1px solid #f1f5f9;
    color: #1e293b;
  }

  tbody tr:hover {
    background-color: #f8fafc;
  }
`;

const SearchInput = styled.input`
  padding: 10px 15px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  width: 100%;
  max-width: 350px;
  margin-bottom: 15px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

function SubirValidar() {
  const [file, setFile] = useState(null);
  const [datos, setDatos] = useState([]);
  const [mes, setMes] = useState('');
  const fileInputRef = useRef(null);
  const [filtro, setFiltro] = useState('');

  const handleDrop = (e) => {
    e.preventDefault();
    setFile(e.dataTransfer.files[0]);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/registros', formData);
      Swal.fire({ icon: 'success', title: 'Éxito', text: 'Archivo subido correctamente', timer: 2000, showConfirmButton: false });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrió un error al subir el archivo' });
    }
  };

  const validarMes = async () => {
    try {
      const res = await api.get(`/registros/validados?mes=${mes}`);
      if (res.data.length === 0) {
        Swal.fire({ icon: 'info', title: 'Sin datos', text: 'No hay registros para este mes', timer: 2500, showConfirmButton: false });
      } else {
        setDatos(res.data);
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Ocurrió un error al consultar los datos' });
    }
  };

  const exportarPDF = () => {
    if (datos.length === 0) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    const fechaReporte = dayjs(mes + '-01').format('MMMM YYYY').toUpperCase();
    doc.setFontSize(16);
    doc.text(`REPORTE GENERAL DE ASISTENCIA - ${fechaReporte}`, 14, 15);
    const diasEnMes = dayjs(mes + '-01').daysInMonth();
    const headers = [['Docente', ...Array.from({ length: diasEnMes }, (_, i) => (i + 1).toString()), 'F', 'A', 'P']];
    const body = datos.map(d => {
      const fila = [d.nombre_completo];
      for (let i = 1; i <= diasEnMes; i++) {
        const fechaStr = dayjs(mes + '-01').date(i).format('YYYY-MM-DD');
        const manual = d.detalle_manual?.find(m => m.fecha === fechaStr);
        if (manual) fila.push(manual.tipo);
        else if (d.asistencias_bio?.includes(fechaStr)) fila.push('.');
        else fila.push('');
      }
      fila.push(d.faltas, d.atrasos, d.permisos);
      return fila;
    });
    autoTable(doc, {
      head: headers, body: body, startY: 25,
      styles: { fontSize: 6.5, cellPadding: 1.5, halign: 'center', valign: 'middle', lineWidth: 0.1, lineColor: [200, 200, 200] },
      headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left', cellWidth: 35, fontStyle: 'bold' } },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const text = data.cell.text[0];
          if (text === 'F') { data.cell.styles.textColor = [211, 47, 47]; data.cell.styles.fontStyle = 'bold'; }
          if (text === 'A') { data.cell.styles.textColor = [245, 124, 0]; data.cell.styles.fontStyle = 'bold'; }
          if (text === 'P') { data.cell.styles.textColor = [56, 142, 60]; data.cell.styles.fontStyle = 'bold'; }
          if (text === '.') { data.cell.styles.textColor = [100, 100, 100]; }
        }
      }
    });
    doc.save(`Reporte_General_${mes}.pdf`);
  };

  return (
    <Container>
      <Card>
        <Title>📁 Subir archivo biométrico (.dat)</Title>
        <DropArea onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onClick={() => fileInputRef.current.click()}>
          {file ? <strong>{file.name}</strong> : 'Arrastra aquí el archivo o haz clic para seleccionar'}
        </DropArea>
        <FileInput ref={fileInputRef} type="file" accept=".dat" onChange={handleFileChange} />
        <Button onClick={handleUpload} disabled={!file}>🚀 Subir Archivo</Button>
      </Card>

      <Card>
        <Title>📅 Validar entradas y salidas</Title>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
          <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #e2e8f0' }} />
          <Button onClick={validarMes} style={{ backgroundColor: '#4f46e5' }}>🔍 Validar</Button>
          <Button onClick={() => window.open(`${API_URL}/registros/reporte-excel?mes=${mes}`, '_blank')} style={{ backgroundColor: '#16a34a' }} disabled={!mes}>📥 Excel</Button>
          <Button onClick={exportarPDF} style={{ backgroundColor: '#dc2626' }} disabled={datos.length === 0}>📄 PDF</Button>
        </div>

        {datos.length > 0 && (
          <>
            <SearchInput type="text" placeholder="🔍 Buscar docente o estado..." value={filtro} onChange={(e) => setFiltro(e.target.value)} />
            <ResponsiveTable>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Estado</th>
                    <th>Docente</th>
                    <th>Entradas</th>
                    <th>Salidas</th>
                    <th>Asist.</th>
                    <th>Esp.</th>
                    <th>Faltas</th>
                    <th>Atrasos</th>
                    <th>Permisos</th>
                  </tr>
                </thead>
                <tbody>
                  {datos
                    .filter(r => r.nombre_completo.toUpperCase().includes(filtro.toUpperCase()) || (r.valido ? 'sí' : 'no').includes(filtro.toLowerCase()))
                    .map((r, i) => (
                      <tr key={i}>
                        <td style={{ color: r.valido ? '#16a34a' : '#dc2626', fontWeight: 'bold', fontSize: '0.75rem' }}>{r.valido ? 'OK' : 'INC.'}</td>
                        <td style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: 'bold', color: '#1e293b', fontSize: '0.85rem' }}>{r.nombre_completo}</div>
                          <div style={{ fontSize: '0.7rem', color: '#64748b' }}>CI: {r.ci}</div>
                        </td>
                        <td>{r.entradas}</td>
                        <td>{r.salidas}</td>
                        <td>{r.dias_asistidos}</td>
                        <td>{r.dias_esperados}</td>
                        <td style={{ color: r.faltas > 0 ? '#dc2626' : 'inherit', fontWeight: r.faltas > 0 ? 'bold' : 'normal' }}>{r.faltas}</td>
                        <td style={{ color: r.atrasos > 0 ? '#ea580c' : 'inherit', fontWeight: r.atrasos > 0 ? 'bold' : 'normal' }}>{r.atrasos}</td>
                        <td style={{ color: r.permisos > 0 ? '#16a34a' : 'inherit', fontWeight: r.permisos > 0 ? 'bold' : 'normal' }}>{r.permisos}</td>
                      </tr>
                    ))}
                </tbody>
              </StyledTable>
            </ResponsiveTable>
          </>
        )}
      </Card>
    </Container>
  );
}

export default SubirValidar;
